import styled from 'styled-components';
import { breakpoints } from '@avangard/ui/utils';

import { PageFilters } from '@modules/shared/organisms';
import { useCurrentUser } from '@modules/user/hooks';
import { useStudentsTableFiltersQuery } from '@modules/students/hooks';
import { UserGrantCategoryToken, UserGrantActionToken } from '@modules/types/graphql';
import { ToolbarFilters } from './toolbar-filters';
import { DrawerFilters } from './drawer-filters';
import { useCurrentLap } from '@modules/lap/hooks';
import { ButtonStudentsImport } from '@modules/students/moleculas/button-students-import';

type StudentsFiltersProps = {
    isEljur?: boolean;
};

const initialFilters = {
    fullName: '',
    schoolLocationRegionId: '',
    schoolLocationId: '',
    schoolType: '',
    schoolId: '',
    eljurRole: '',
    schoolClass: '',
};

const Root = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    width: 100%;

    ${breakpoints.down('sm')} {
        flex-wrap: wrap;
    }
`;

const Actions = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    width: 100%;

    & > * {
        &:not(:last-child) {
            margin-inline-end: 8px;
        }
    }

    ${breakpoints.down('sm')} {
        margin-top: -70px;
        margin-inline-start: 142px;
    }

    ${breakpoints.down('xs')} {
        justify-content: flex-start;
        margin-bottom: 28px;
    }
`;

const StudentsFilters = (props: StudentsFiltersProps): React.ReactElement => {
    const { isEljur } = props;

    const { currentUser } = useCurrentUser();
    const { studentGrants } = currentUser.can({
        [UserGrantCategoryToken.student]: [
            UserGrantActionToken.search,
            UserGrantActionToken.filter,
            UserGrantActionToken.import,
            UserGrantActionToken.downloadTemplate,
        ],
    });

    const { filters: queryFilters } = useStudentsTableFiltersQuery();

    const { currentLap, loading: currentLapLoading } = useCurrentLap();

    const hasCurrentLap = !currentLapLoading && currentLap.exists();

    const canDataControl =
        hasCurrentLap && (studentGrants.canImport || studentGrants.canDownloadTemplate);

    return (
        <Root>
            <PageFilters
                queryFilters={queryFilters}
                initials={initialFilters}
                drawerFiltersKeys={[
                    'schoolLocationRegionId',
                    'schoolLocationId',
                    'schoolType',
                    'schoolId',
                    'companies',
                    'platoons',
                    'eljurRole',
                    'schoolClass',
                ]}
            >
                <ToolbarFilters
                    canSearch={studentGrants.canSearch}
                    hideDrawerFilterButton={!studentGrants.canFilter}
                />

                <Actions>
                    {canDataControl ? (
                        <ButtonStudentsImport
                            canImport={studentGrants.canImport}
                            canDownloadTemplate={studentGrants.canDownloadTemplate}
                        />
                    ) : null}
                </Actions>

                {studentGrants.canFilter ? <DrawerFilters isEljur={isEljur} /> : null}
            </PageFilters>
        </Root>
    );
};

export { StudentsFilters };
